// # Popover — 단추에 매달려 뜨는 판(docs/design.md)
//
// 네이티브 `popover` 속성이 뜨고 닫는 일을 한다: 맨 위 층에 올라 `overflow: hidden` 에 잘리지
// 않고, 바깥을 누르거나 Esc 면 닫히고(light dismiss), 닫힐 때 초점이 안에 있었으면 단추로 돌아간다.
// 단추는 `popovertarget` 으로 판을 가리키므로 여닫기에 스크립트가 없다.
//
// 자리만 우리가 잡는다. CSS 앵커 위치(anchor positioning)는 WKWebView 가 macOS 26 부터라 데스크톱
// 셸의 옛 macOS 에서 판이 화면 한가운데 뜬다. 그래서 `place` 한 함수가 단추의 사각형·판의 크기·
// 창 크기로 좌표를 셈하고, 모자라면 반대쪽으로 **뒤집고**, 그래도 넘치면 창 안으로 **민다**.
// 좌표는 `--pop-x`·`--pop-y` 로 꽂는다 — 모양은 CSS 의 것이다.
//
// - 여는 방법은 `trigger(props)` 에 받은 속성을 단추에 펼치는 것 하나다. `aria-expanded` 가 열림을
//   따라가고 `aria-controls` 가 판을 가리킨다.
// - 안의 내용은 늘 그린다(닫혀 있으면 브라우저가 숨기고 접근성 트리에서도 뺀다). 판의 크기를 재야
//   자리를 잡을 수 있고, 서버 렌더 시험이 판 안의 글자를 볼 수 있다.
// - 안에서 닫아야 하면 `children` 을 함수로 준다 — `close` 를 받는다.
// - 컴포넌트 `Popover` 는 `popover_box.tsx` 에 산다. 이 파일의 자리 셈은 풍선 도움말이 첫 화면에서
//   쓰므로, 같은 파일에 두면 lazy 판이 `Popover` 를 부르는 순간 첫 화면에 같이 실린다(docs/design2.md E35b).

import { type RefObject, useEffect, useEffectEvent } from 'react'

export type PopSide = 'top' | 'bottom' | 'left' | 'right'
export type PopAlign = 'start' | 'center' | 'end'

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

export interface Size {
  w: number
  h: number
}

/** 창 가장자리에서 띄우는 틈. */
export const VIEW_MARGIN = 8

const OPPOSITE: Record<PopSide, PopSide> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(v, Math.max(lo, hi)))
}

/** `side` 쪽에 판이 들어갈 자리가 얼마나 있는가(음수면 모자란다). */
function room(side: PopSide, anchor: Rect, pop: Size, view: Size, gap: number): number {
  if (side === 'bottom') return view.h - (anchor.y + anchor.h + gap) - pop.h - VIEW_MARGIN
  if (side === 'top') return anchor.y - gap - pop.h - VIEW_MARGIN
  if (side === 'right') return view.w - (anchor.x + anchor.w + gap) - pop.w - VIEW_MARGIN
  return anchor.x - gap - pop.w - VIEW_MARGIN
}

function along(start: number, length: number, size: number, align: PopAlign): number {
  if (align === 'start') return start
  if (align === 'end') return start + length - size
  return start + (length - size) / 2
}

/**
 * 판을 둘 좌표. 고른 쪽에 자리가 모자라고 반대쪽이 더 넉넉하면 뒤집는다.
 * 마지막에 창 안으로 민다 — 판이 창보다 크면 왼쪽·위 가장자리에 붙는다.
 */
export function place(
  anchor: Rect,
  pop: Size,
  view: Size,
  side: PopSide,
  align: PopAlign,
  gap = 4,
): { x: number; y: number; side: PopSide } {
  const flip = OPPOSITE[side]
  const s =
    room(side, anchor, pop, view, gap) < 0 &&
    room(flip, anchor, pop, view, gap) > room(side, anchor, pop, view, gap)
      ? flip
      : side
  let x: number
  let y: number
  if (s === 'top' || s === 'bottom') {
    x = along(anchor.x, anchor.w, pop.w, align)
    y = s === 'bottom' ? anchor.y + anchor.h + gap : anchor.y - gap - pop.h
  } else {
    y = along(anchor.y, anchor.h, pop.h, align)
    x = s === 'right' ? anchor.x + anchor.w + gap : anchor.x - gap - pop.w
  }
  return {
    x: Math.round(clamp(x, VIEW_MARGIN, view.w - pop.w - VIEW_MARGIN)),
    y: Math.round(clamp(y, VIEW_MARGIN, view.h - pop.h - VIEW_MARGIN)),
    side: s,
  }
}

/** 판을 단추 옆에 세운다. 좌표는 CSS 변수로만 — 모양은 CSS 가 쥔다. */
export function put_beside(
  el: HTMLElement,
  anchor: Element,
  side: PopSide,
  align: PopAlign,
  gap?: number,
): void {
  const a = anchor.getBoundingClientRect()
  const view = el.ownerDocument.documentElement
  const at = place(
    { x: a.left, y: a.top, w: a.width, h: a.height },
    { w: el.offsetWidth, h: el.offsetHeight },
    { w: view.clientWidth, h: view.clientHeight },
    side,
    align,
    gap,
  )
  el.style.setProperty('--pop-x', `${at.x}px`)
  el.style.setProperty('--pop-y', `${at.y}px`)
  el.dataset.side = at.side
}

/** `trigger` 가 단추에 펼칠 속성. */
export interface PopoverTriggerProps {
  ref: RefObject<HTMLButtonElement | null>
  popoverTarget: string
  'aria-expanded': boolean
  'aria-controls': string
  'aria-haspopup': 'dialog' | 'menu' | 'listbox'
}

/** 열리고 닫힐 때마다 자리를 잡고, 열린 동안 창이 바뀌면 다시 잡는다. */
export function usePopoverPlacement(
  panel: RefObject<HTMLElement | null>,
  anchor: RefObject<Element | null>,
  side: PopSide,
  align: PopAlign,
  on_toggle: (open: boolean) => void,
): void {
  const place_now = useEffectEvent(() => {
    const el = panel.current
    const at = anchor.current
    if (el !== null && at !== null) put_beside(el, at, side, align)
  })
  const toggled = useEffectEvent((open: boolean) => on_toggle(open))
  useEffect(() => {
    const el = panel.current
    if (el === null) return
    const again = () => place_now()
    const before = (e: Event) => {
      // 보이기 직전 — 판의 크기는 아직 0 이지만 단추 곁에 먼저 세워 둔다(한 장면이라도 가운데 뜨지 않게).
      if ((e as ToggleEvent).newState === 'open') place_now()
    }
    const after = (e: Event) => {
      const open = (e as ToggleEvent).newState === 'open'
      if (open) {
        place_now()
        window.addEventListener('resize', again)
        window.addEventListener('scroll', again, true)
      } else {
        window.removeEventListener('resize', again)
        window.removeEventListener('scroll', again, true)
      }
      toggled(open)
    }
    el.addEventListener('beforetoggle', before)
    el.addEventListener('toggle', after)
    return () => {
      el.removeEventListener('beforetoggle', before)
      el.removeEventListener('toggle', after)
      window.removeEventListener('resize', again)
      window.removeEventListener('scroll', again, true)
    }
  }, [panel])
}
